import React from 'react';
import { useTranslation } from 'react-i18next';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import type { SegmentedControlOption } from '@/components/ui/SegmentedControl';
import { TOURNAMENT_STATUSES } from '@/constants';
import type { TournamentStatus } from '@/constants';
import styles from './styles.module.scss';

export interface TournamentStatusFilterProps {
  status: TournamentStatus | null;
  onStatusChange: (value: TournamentStatus | null) => void;
  /** Per-status totals for the current search; segments render without counts until known. */
  statusCounts?: Record<TournamentStatus, number>;
  /** Builds `data-testid`s: `${idPrefix}-status-all`, `-status-active`, … */
  idPrefix: string;
}

const STATUSES = Object.values(TOURNAMENT_STATUSES) as TournamentStatus[];
/** Segment value for "no status filter". */
const ALL = 'all';
type StatusOption = TournamentStatus | typeof ALL;

/** All / Active / Upcoming / Closed segmented control with counts. */
export const TournamentStatusFilter: React.FC<TournamentStatusFilterProps> = ({
  status,
  onStatusChange,
  statusCounts,
  idPrefix,
}) => {
  const { t } = useTranslation('tournament');

  const allCount = statusCounts
    ? STATUSES.reduce((sum, value) => sum + statusCounts[value], 0)
    : undefined;

  const options: SegmentedControlOption<StatusOption>[] = [
    {
      value: ALL,
      label: t('tournament:list.filterAll'),
      count: allCount,
      testId: `${idPrefix}-status-all`,
    },
    ...STATUSES.map((value) => ({
      value,
      label: t(`tournament:list.filter.${value}`),
      count: statusCounts?.[value],
      testId: `${idPrefix}-status-${value}`,
    })),
  ];

  return (
    <SegmentedControl<StatusOption>
      options={options}
      value={status ?? ALL}
      onChange={(value) => onStatusChange(value === ALL ? null : value)}
      ariaLabel={t('tournament:list.statusFilterLabel')}
      stretch
      className={styles.status}
    />
  );
};

export default TournamentStatusFilter;
